import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { constants, currencyString } from "@qgiv/core-js";
import { ThemeType } from "../../../redux/constants";
import { selectReceipt } from "../../../redux/slices/receiptSlice";
import { selectAllP2pSettings } from "../../../redux/slices/p2pSettingsSlice";
import { selectAllFormSettings } from "../../../redux/slices/formSettingsSlice";
import {
    selectCmsControlByIdByPage,
    selectThemeSettings,
} from "../../../redux/slices/cmsSettingsSlice";
import { selectIsCms } from "../../../redux/slices/configSlice";
import ReceiptDetailsGifts from "./ReceiptDetailsGifts";

const ConnectedReceiptDetailsGifts = ({ controlId }) => {
    const {
        ENUMS: { DefaultView },
    } = constants;
    const receipt = useSelector(selectReceipt);
    const {
        transaction: { donations = [] },
        feeCoverage,
        total, // this is the subtotal of the gifts section (donations + gift assist)
    } = receipt;
    const p2pSettings = useSelector(selectAllP2pSettings);
    const { enableOptionalDonation = false } = p2pSettings;
    const formSettings = useSelector(selectAllFormSettings);
    const { currency } = formSettings;
    const formSettingsFeeCoverage = formSettings.feeCoverage;
    const control = useSelector((state) =>
        selectCmsControlByIdByPage(
            controlId,
            DefaultView.STANDARD_REGISTRATION_RECEIPT,
            state,
        ),
    );
    const {
        options: { giftAssistAdditionalText },
    } = control;

    const themeSettings = useSelector((state) =>
        selectThemeSettings(ThemeType.SUMMARY, state),
    );

    const {
        p2pGeneral: { subtotalLabel = "Subtotal" },
        p2pGifts: {
            giftSubsectionHeading = "Gifts",
            giftAssistLabel = "Extra gift to offset fees",
        },
    } = themeSettings;

    const formattedFeeCoverage = currencyString(feeCoverage, currency);

    const isCms = useSelector(selectIsCms);
    // The transaction values for feeCoverage and donations should
    // override form settings when viewing an actual receipt
    // but we want to conditionally show sections on cms based on form settings
    // because the default values will always be there
    const isDonationVisible = !isCms || (enableOptionalDonation && isCms);
    const isGiftAssistVisible = !!(
        (!isCms && feeCoverage) ||
        (Number(formSettingsFeeCoverage) > 0 && isCms)
    );
    const isVisible =
        total > 0 || (isCms && (isDonationVisible || isGiftAssistVisible));

    const receiptDetailsGiftsProps = {
        controlId,
        donations,
        total,
        feeCoverage,
        formattedFeeCoverage,
        giftAssistLabel,
        giftAssistAdditionalText,
        giftSubsectionHeading,
        subtotalLabel,
        isVisible,
        isDonationVisible,
        isGiftAssistVisible,
    };

    return <ReceiptDetailsGifts {...receiptDetailsGiftsProps} />;
};

ConnectedReceiptDetailsGifts.propTypes = {
    controlId: PropTypes.number.isRequired,
};

export default ConnectedReceiptDetailsGifts;
